import React, { useState } from 'react';
import { BsCircle } from 'react-icons/bs';
import { HiPlus, HiPrinter } from 'react-icons/hi';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import ApiErrorAlert from 'src/components/_common/ApiErrorAlert/ApiErrorAlert';
import UserBadge from 'src/components/_common/Badge/UserBadge';
import Button from 'src/components/_common/Button/Button';
import Card from 'src/components/_common/Card/Card';
import PageHeader from 'src/components/_common/PageHeader/PageHeader';
import Spinner from 'src/components/_common/Spinner/Spinner';
import { StudentUserReferences } from 'src/models/user';
import { isAttendanceFeatureEnabled } from 'src/utils/featureFlags';
import ShowSigningFormPdf from '../ShowSigningFormPdf';
import ShowStudentPdf from '../ShowStudentPdf';
import AssignmentFormsLists from './AssignmentFormsLists';
import SupervisorBadge from './SupervisorBadge';
import SupervisorConfirmationModal from './SupervisorConfirmationModal';
import { useFormListPageData } from './queries';
import InvitationStatusLabel from './InvitationStatusLabel';

const StudentForms: React.FC = () => {
  const query = useFormListPageData();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSupervisor, setSelectedSupervisor] =
    useState<StudentUserReferences | null>(null);

  function navigateToSigningForm(classId: number, studentUserId: number) {
    navigate(`/student/class/${classId}/student/${studentUserId}/signingform`);
  }

  function navigateToAttendance(classId: number) {
    navigate(`/student/class/${classId}/attendance`);
  }

  function navigateToEditSupervisor() {
    navigate('/student/supervisor/invite');
  }

  function openSupervisorModal(supervisor: StudentUserReferences) {
    setSelectedSupervisor(supervisor);
    setIsModalOpen(true);
  }

  function closeSupervisorModal() {
    setIsModalOpen(false);
    setSelectedSupervisor(null);
  }

  if (query.isLoading) return <Spinner />;
  if (query.isError) {
    return (
      <ApiErrorAlert
        error={query.error}
        message={formatMessage({ id: 'formListPageErrorMessage' })}
      />
    );
  }
  const { subject } = query.data.class;
  const teacherUserId = query.data.user.studentReferences?.teacherUserId;
  const primarySupervisorUserId =
    query.data.user.studentReferences?.advisorUserId;

  const handlePdf = () => {
    ShowStudentPdf(query.data.class.id, query.data.user.id);
  };
  const handleSigningFormPdf = () => {
    ShowSigningFormPdf(query.data.class.id, query.data.user.id);
  };

  const supervisorsSortedByPrimary =
    query.data.user.studentUserReferences?.sort((a, b) => {
      if (a.userId === primarySupervisorUserId) return -1;
      if (b.userId === primarySupervisorUserId) return 1;
      return 0;
    });

  return (
    <>
      <PageHeader title={subject?.subjectCode} subTitle={subject?.name}>
        <section>
          <Button onClick={handlePdf} iconLeft={HiPrinter} className="mr-2">
            <FormattedMessage id="forms" />
          </Button>
          <Button onClick={handleSigningFormPdf} iconLeft={HiPrinter}>
            <FormattedMessage id="signing-form-header" />
          </Button>
        </section>
      </PageHeader>

      <div className="flex w-full justify-center">
        <div>{teacherUserId && <UserBadge id={teacherUserId} />}</div>
        <div>
          {primarySupervisorUserId ? (
            <div>
              {supervisorsSortedByPrimary?.map(supervisor => (
                <SupervisorBadge
                  key={supervisor.userId}
                  id={supervisor.userId}
                  onEditClick={
                    supervisor.userId !== primarySupervisorUserId
                      ? () => {
                          openSupervisorModal(supervisor);
                        }
                      : navigateToEditSupervisor
                  }
                  isSecondary={supervisor.userId !== primarySupervisorUserId}
                  data-testid="supervisor-badge"
                />
              ))}
            </div>
          ) : (
            <Button onClick={navigateToEditSupervisor} size="small" className="ml-2 mt-2 relative" style={{ bottom: 2 }} data-testid="invite-supervisor-button">
              <HiPlus className="mr-1" />
              {formatMessage({ id: 'formListPageInviteSupervisorButtonText' })}
            </Button>
          )}
          <InvitationStatusLabel />
        </div>
      </div>
      {query.data.signingForm && (
        <div data-testid="signing-form-card-container">
          <Card
            className="mt-8"
            primary={false}
            onClick={() =>
              navigateToSigningForm(query.data.class.id, query.data!.user.id)
            }
            header={formatMessage({ id: 'signing-form-header' })}
          />
        </div>
      )}
      {isAttendanceFeatureEnabled() && (
        <div data-testid="attendance-card-container">
          <Card
            primary={false}
            onClick={() => navigateToAttendance(query.data!.class.id)}
            header={formatMessage({ id: 'attendance-page-title' })}
          />
        </div>
      )}
      <AssignmentFormsLists
        classData={query.data.class}
        signingForm={query.data.signingForm}
        supervisorExists={!!primarySupervisorUserId}
      />

      {/* Supervisor Confirmation Modal */}
      {selectedSupervisor && (
        <SupervisorConfirmationModal
          isOpen={isModalOpen}
          close={closeSupervisorModal}
          supervisor={selectedSupervisor}
          classId={query.data.class.id}
        />
      )}
    </>
  );
};

export default StudentForms;
